/* FlytLinkPromoCard · shared promo card for FLYT ID™ (free live link)
 *
 * Renders a compact, state-aware card that adapts its copy + CTA to the
 * viewer's current subscription/claim state. Used on:
 *   • Athlete dashboard              (state='claimed_free' or 'premium')
 *   • Unclaimed public profile       (state='unclaimed')
 *   • Bottom of any athlete surface  (state='discovery')
 *
 * Props:
 *   variant: 'discovery' | 'unclaimed' | 'claimed_free' | 'premium' | 'expired'
 *   compact:  boolean — smaller footprint for inline placement
 *   onDismiss(): if provided, adds a close X. Dismissal is session-local.
 *   completion: 0-100 — % completion for claimed_free / premium
 *   onCta(),  onSecondaryCta():   click hooks
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, ArrowRight, ShieldCheck, LayoutTemplate, Share2, LineChart } from 'lucide-react';

const COPY = {
  discovery: {
    kicker: 'Built for athletes',
    headline: 'More than a link in bio.',
    body: 'Build one verified page for your story, highlights, stats, recruiting information, social links, and opportunities.',
    price: 'Free',
    ctaPrimary: 'Build your FLYT ID™',
    ctaSecondary: 'See example',
  },
  unclaimed: {
    kicker: 'Are you this athlete?',
    headline: 'Claim this profile.',
    body: 'Turn this public profile into your official FLYT ID™ — one verified home for your story, stats, highlights, and inquiries.',
    price: '',
    ctaPrimary: 'Claim this profile',
    ctaSecondary: 'See what you can build',
  },
  // iter886 · The FLYT ID™ live link is free.
  claimed_free: {
    kicker: 'Make this profile yours',
    headline: 'Build your FLYT ID™.',
    body: 'Add your custom link, highlights, stats, recruiting information, stories, social links, and inquiries — then share it anywhere.',
    price: 'Free',
    ctaPrimary: 'Build your FLYT ID™ — free',
    ctaSecondary: 'View my profile',
  },
  premium: {
    kicker: 'FLYT ID™ · Active',
    headline: 'Keep building.',
    body: 'Your FLYT ID™ is live. Complete your recommended sections to keep growing your reach.',
    price: '',
    ctaPrimary: 'Continue building',
    ctaSecondary: 'View my link',
  },
  expired: {
    kicker: 'Your FLYT ID™',
    headline: 'Turn your link back on.',
    body: 'Your setup is saved. Your FLYT ID™ is free — switch it back on for coaches, brands, and fans.',
    price: 'Free',
    ctaPrimary: 'Turn it on — free',
    ctaSecondary: 'View setup',
  },
};

const FlytLinkPromoCard = ({
  variant = 'discovery',
  compact = false,
  onDismiss,
  completion,
  onCta,
  onSecondaryCta,
  ctaHref,
  secondaryHref,
  className = '',
  testid = 'flyt-link-promo',
}) => {
  const [hidden, setHidden] = useState(false);
  if (hidden) return null;

  const c = COPY[variant] || COPY.discovery;
  const rootPadding = compact ? 'p-4 sm:p-5' : 'p-6 sm:p-8';
  const headlineSize = compact ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-3xl';

  const PrimaryTag = ctaHref ? Link : 'button';
  const SecondaryTag = secondaryHref ? Link : 'button';

  return (
    <section
      className={`relative rounded-3xl bg-gradient-to-br from-blue-500/[0.08] via-white/[0.03] to-black ring-1 ring-blue-400/25 ${rootPadding} ${className}`}
      data-testid={testid}
    >
      {onDismiss && (
        <button
          type="button"
          onClick={() => { setHidden(true); onDismiss(); }}
          className="absolute top-3 right-3 w-7 h-7 rounded-full bg-black/40 ring-1 ring-white/10 text-white/60 hover:text-white flex items-center justify-center"
          aria-label="Dismiss"
          data-testid={`${testid}-dismiss`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}

      <p className="text-[10px] uppercase tracking-[0.28em] font-black text-blue-300 mb-2">{c.kicker}</p>
      <h3 className={`font-display uppercase leading-[0.98] text-white ${headlineSize}`}>{c.headline}</h3>
      <p className="mt-3 text-white/75 text-sm leading-relaxed max-w-xl">{c.body}</p>

      {typeof completion === 'number' && variant !== 'unclaimed' && variant !== 'discovery' && (
        <div className="mt-4 max-w-sm">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] uppercase tracking-widest text-white/60 font-bold">Your FLYT ID™</span>
            <span className="text-[11px] text-white font-mono">{Math.max(0, Math.min(100, completion))}%</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-blue-400" style={{ width: `${Math.max(0, Math.min(100, completion))}%` }} />
          </div>
        </div>
      )}

      {c.price && (
        <p className="mt-4 inline-block px-2.5 py-1 rounded-full ring-1 ring-white/15 bg-white/5 text-white text-[10px] uppercase tracking-widest font-bold">
          {c.price}
        </p>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        {ctaHref ? (
          <Link
            to={ctaHref}
            onClick={(e) => {
              // iter484 · Medium haptic — tapping "Build your FLYT ID™"
              // is a big-moment decision; the confident thud reinforces
              // it on native iOS.
              try { import('../../lib/nativeBridge').then((m) => m.haptic('medium')).catch(() => {}); } catch { /* noop */ }
              onCta?.(e);
            }}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white text-black text-[11px] font-black uppercase tracking-widest hover:bg-blue-200 transition"
            data-testid={`${testid}-primary`}
          >
            {c.ctaPrimary} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        ) : (
          <PrimaryTag
            type="button"
            onClick={(e) => {
              try { import('../../lib/nativeBridge').then((m) => m.haptic('medium')).catch(() => {}); } catch { /* noop */ }
              onCta?.(e);
            }}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white text-black text-[11px] font-black uppercase tracking-widest hover:bg-blue-200 transition"
            data-testid={`${testid}-primary`}
          >
            {c.ctaPrimary} <ArrowRight className="w-3.5 h-3.5" />
          </PrimaryTag>
        )}
        {secondaryHref ? (
          <Link
            to={secondaryHref}
            onClick={onSecondaryCta}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full ring-1 ring-white/20 bg-white/5 text-white text-[11px] font-bold uppercase tracking-widest hover:bg-white/15 transition"
            data-testid={`${testid}-secondary`}
          >
            {c.ctaSecondary}
          </Link>
        ) : (
          <SecondaryTag
            type="button"
            onClick={onSecondaryCta}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full ring-1 ring-white/20 bg-white/5 text-white text-[11px] font-bold uppercase tracking-widest hover:bg-white/15 transition"
            data-testid={`${testid}-secondary`}
          >
            {c.ctaSecondary}
          </SecondaryTag>
        )}
      </div>

      {/* Four-outcome grid — only shown on non-compact discovery variant. */}
      {!compact && variant === 'discovery' && (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { Icon: ShieldCheck,     title: 'Be Found',        body: 'Verified profile and searchable FLYT presence.' },
            { Icon: LayoutTemplate,  title: 'Tell Your Story', body: 'Bio, milestones, stories, photos, and videos.' },
            { Icon: Share2,          title: 'Share Everything',body: 'One link for Hudl, stats, recruiting, socials, and inquiries.' },
            { Icon: LineChart,       title: 'See Who Cares',   body: 'Profile views, engagement, link clicks, and requests.' },
          ].map(({ Icon, title, body }) => (
            <div key={title} className="rounded-2xl bg-white/[0.03] ring-1 ring-white/10 p-3">
              <Icon className="w-4 h-4 text-blue-300 mb-2" />
              <p className="font-display text-xs uppercase tracking-widest text-white">{title}</p>
              <p className="text-[11px] text-white/60 mt-1 leading-snug">{body}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default FlytLinkPromoCard;
